import { useEffect, useState } from 'react';
import {
  ActionsDialogue, Bouton, CaseACocher, Dialogue, Message, Selecteur,
} from '@journal/ui';
import { messageErreur, useSession } from '../lib/session.tsx';
import { useEcriture } from '../lib/donnees.ts';

/**
 * Reprendre une année précédente dans l'année qu'on vient d'ouvrir.
 *
 * ── Tout coché par défaut ───────────────────────────────────────────────────
 * Le cas courant est une école qui repart avec la même équipe et veut
 * retrouver son travail : tous les enseignants et tous les modules sont donc
 * cochés, et l'on décoche ce qui ne doit pas suivre — un collègue parti, un
 * module qu'on veut refaire à neuf. Un enseignant qui a quitté l'école part
 * décoché : lui recréer une classe peuplerait l'année d'un compte absent.
 *
 * ── Voir avant de faire ─────────────────────────────────────────────────────
 * « Voir ce qui sera repris » exécute la reprise pour de vrai côté serveur,
 * lit le compte rendu, puis annule tout : les chiffres annoncés sont ceux que
 * produira la reprise, pas une estimation.
 */

export const MODULES = [
  { valeur: 'programmation', texte: 'Programmation' },
  { valeur: 'progressions', texte: 'Progressions', aide: 'Recalées sur les semaines de la nouvelle année, période par période.' },
  { valeur: 'emploiDuTemps', texte: 'Emploi du temps' },
  { valeur: 'cahierJournal', texte: 'Cahier journal', aide: 'Chaque séance au même jour de la même semaine de période, à refaire.' },
  { valeur: 'evaluation', texte: 'Évaluation', aide: 'Les contrôles, sans les résultats des élèves.' },
  { valeur: 'eleves', texte: 'Élèves', aide: 'La liste de chaque classe, sans leurs évaluations.' },
  { valeur: 'heures', texte: '108 heures', aide: 'Les activités qui se répètent, aux mêmes jours de la semaine.' },
] as const;

type Module = (typeof MODULES)[number]['valeur'];

const LIBELLE_MODULE: Record<Module, string> = {
  programmation: 'blocs de programmation',
  progressions: 'séquences',
  emploiDuTemps: 'créneaux',
  cahierJournal: 'séances',
  evaluation: 'contrôles',
  eleves: 'élèves',
  heures: 'activités',
};

interface Enseignant { userId: string | null; nom: string; dansLEcole: boolean; classes: string[] }
interface Bilan { copies: number; ecartes: number; note?: string }
interface Rapport {
  classes: Array<{ nom: string; enseignant: string | null; creee: boolean; modules: Partial<Record<Module, Bilan>> }>;
  heures: Bilan | null;
  programmeDifferent: boolean;
  applique: boolean;
}

/** Une ligne lisible du compte rendu d'une classe. */
export function resumeClasse(modules: Partial<Record<Module, Bilan>>): string {
  const parts = (Object.entries(modules) as Array<[Module, Bilan]>)
    .filter(([, b]) => b.copies > 0)
    .map(([m, b]) => `${b.copies} ${LIBELLE_MODULE[m]}`);
  return parts.length > 0 ? parts.join(' · ') : 'rien à reprendre';
}

export function DialogueReprise({ annee, surFermeture, surTerminee }: {
  /** L'année d'arrivée. `null` : fenêtre fermée. */
  annee: { id: string; label: string } | null;
  surFermeture: () => void;
  surTerminee: (message: string) => void;
}) {
  const { api } = useSession();
  const { executer, enCours, erreur } = useEcriture();
  const [sources, setSources] = useState<Array<{ id: string; label: string }>>([]);
  const [source, setSource] = useState<string | null>(null);
  const [enseignants, setEnseignants] = useState<Enseignant[]>([]);
  const [coches, setCoches] = useState<Set<string>>(new Set());
  const [modules, setModules] = useState<Set<Module>>(new Set(MODULES.map((m) => m.valeur)));
  const [apercu, setApercu] = useState<Rapport | null>(null);

  const cle = (e: Enseignant) => e.userId ?? 'sans';

  const [erreurChargement, setErreurChargement] = useState<string | null>(null);

  async function charger(depuis?: string) {
    if (!annee) return;
    setErreurChargement(null);
    let r: { sources: typeof sources; source: string | null; enseignants: Enseignant[] };
    try {
      r = await api.requete(`/school-years/${annee.id}/reprise${depuis ? `?depuis=${depuis}` : ''}`);
    } catch (e) {
      // Dit, plutôt que tait : une fenêtre vide laisserait croire qu'il n'y a
      // rien à reprendre.
      setErreurChargement(messageErreur(e));
      setSources([]); setSource(null); setEnseignants([]);
      return;
    }
    setSources(r.sources);
    setSource(r.source);
    setEnseignants(r.enseignants);
    setCoches(new Set(r.enseignants.filter((e) => e.dansLEcole).map(cle)));
    setApercu(null);
  }

  useEffect(() => {
    if (!annee) return;
    setModules(new Set(MODULES.map((m) => m.valeur)));
    void charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annee?.id]);

  function basculer<T>(ensemble: Set<T>, valeur: T, fixer: (s: Set<T>) => void) {
    const suivant = new Set(ensemble);
    if (suivant.has(valeur)) suivant.delete(valeur); else suivant.add(valeur);
    fixer(suivant);
    setApercu(null);
  }

  async function lancer(simuler: boolean) {
    if (!annee || !source) return;
    const corps = {
      depuis: source,
      enseignants: enseignants.filter((e) => coches.has(cle(e))).map((e) => e.userId),
      modules: [...modules],
      simuler,
    };
    let rapport: Rapport | null = null;
    const ok = await executer(async () => {
      rapport = await api.requete<Rapport>(`/school-years/${annee.id}/reprise`, { methode: 'POST', corps });
    });
    if (!ok || !rapport) return;
    const r: Rapport = rapport;
    if (simuler) { setApercu(r); return; }
    const libelleSource = sources.find((s) => s.id === source)?.label ?? 'l’année précédente';
    surTerminee(`${libelleSource} reprise dans ${annee.label} : ${r.classes.length} classe${r.classes.length > 1 ? 's' : ''}.`);
  }

  const rien = coches.size === 0 || modules.size === 0;
  const seulEnseignant = enseignants.length <= 1;

  return (
    <Dialogue
      titre={annee ? `Reprendre une année dans ${annee.label}` : 'Reprendre une année'}
      ouvert={annee !== null}
      surFermeture={surFermeture}
    >
      {annee && (
        <div className="flex flex-col gap-4">
          {!source ? (
            !erreurChargement && <Message>Aucune année précédente à reprendre.</Message>
          ) : (
            <>
              <p className="text-[14px] leading-relaxed">
                Retrouvez le travail de l’an dernier : les classes sont recréées dans{' '}
                <strong>{annee.label}</strong>, avec leur contenu. Rien de ce qui existe déjà
                n’est remplacé.
              </p>

              {sources.length > 1 && (
                <Selecteur
                  libelle="Reprendre depuis"
                  value={source}
                  onChange={(e) => void charger(e.target.value)}
                  options={sources.map((s) => ({ valeur: s.id, texte: s.label }))}
                />
              )}

              {!seulEnseignant && (
                <fieldset className="flex flex-col gap-2">
                  <legend className="mb-1 text-[13px] font-semibold">Enseignants</legend>
                  {enseignants.map((e) => (
                    <CaseACocher
                      key={cle(e)}
                      libelle={`${e.nom} · ${e.classes.join(', ')}`}
                      {...(!e.dansLEcole ? { description: 'N’est plus dans l’école.' } : {})}
                      checked={coches.has(cle(e))}
                      onChange={() => basculer(coches, cle(e), setCoches)}
                    />
                  ))}
                </fieldset>
              )}

              <fieldset className="flex flex-col gap-2">
                <legend className="mb-1 text-[13px] font-semibold">Modules</legend>
                {MODULES.map((m) => (
                  <CaseACocher
                    key={m.valeur}
                    libelle={m.texte}
                    {...('aide' in m ? { description: m.aide } : {})}
                    checked={modules.has(m.valeur)}
                    onChange={() => basculer(modules, m.valeur, setModules)}
                  />
                ))}
              </fieldset>

              {apercu && (
                <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-raised)] p-3 text-[13px]">
                  <p className="mb-2 font-semibold">Ce qui sera repris</p>
                  <ul className="flex flex-col gap-1.5">
                    {apercu.classes.map((c) => (
                      <li key={`${c.nom}-${c.enseignant ?? ''}`}>
                        <strong>{c.nom}</strong>
                        {c.enseignant ? ` (${c.enseignant})` : ''}
                        {c.creee ? '' : ' (classe déjà présente, complétée)'}
                        {' : '}
                        {resumeClasse(c.modules)}
                        {(Object.values(c.modules) as Bilan[]).filter((b) => b.note).map((b) => (
                          <span key={b.note} className="block text-[var(--color-fg-muted)]">{b.note}</span>
                        ))}
                      </li>
                    ))}
                    {apercu.heures && apercu.heures.copies > 0 && (
                      <li>{`108 heures : ${apercu.heures.copies} activités`}</li>
                    )}
                  </ul>
                  {apercu.programmeDifferent && (
                    <p className="mt-2 text-[var(--color-fg-muted)]">
                      La nouvelle année suit une autre édition du programme : les textes sont
                      repris, sans leur lien au programme officiel.
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {erreurChargement && <Message ton="erreur">{erreurChargement}</Message>}
          {erreur && <Message ton="erreur">{erreur}</Message>}

          <ActionsDialogue>
            <Bouton variante="ghost" onClick={surFermeture}>Ne rien reprendre</Bouton>
            {source && (
              <>
                <Bouton variante="secondary" chargement={enCours} disabled={rien} onClick={() => void lancer(true)}>
                  Voir ce qui sera repris
                </Bouton>
                <Bouton variante="primary" chargement={enCours} disabled={rien} onClick={() => void lancer(false)}>
                  Reprendre
                </Bouton>
              </>
            )}
          </ActionsDialogue>
        </div>
      )}
    </Dialogue>
  );
}
